import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  MenuItem,
  IconButton,
  Link,
  useTheme,
  useMediaQuery,
  Chip,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  WhatsApp,
  Instagram,
  Facebook,
  YouTube,
  Send,
  CheckCircle
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import bgImg from '../../../assets/images/windows_os_banner.png';
import SEO from '../../common/SEO';

const contactInfo = [
  {
    icon: <Phone />,
    label: 'CALL US',
    value: '+91 91132 99131 and +91 99019 91771',
    color: '#0e6ada',
    href: 'tel:+919113299131'
  },
  {
    icon: <WhatsApp />,
    label: 'WHATSAPP',
    value: 'Chat with us',
    color: '#25D366',
    href: 'https://wa.me/919113299131'
  },
  {
    icon: <Email />,
    label: 'EMAIL US',
    value: 'arinfotechsolution11@gmail.com',
    color: '#0e6ada',
    href: 'mailto:arinfotechsolution11@gmail.com'
  },
  {
    icon: <LocationOn />,
    label: 'VISIT US',
    value: 'Unit 101, Oxford Towers, 139/88, Hal Old Airport Rd Kodihalli, Bangalore, Karnataka, India, 560008',
    color: '#e53935',
    href: 'https://maps.google.com/?q=JP+Nagar+7th+Phase+Bengaluru'
  }
];

const ContactPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [snackOpen, setSnackOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: 'order', message: '' });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSnackOpen(true);
    setFormData({ name: '', email: '', phone: '', subject: 'order', message: '' });
  };

  return (
    <Box>
      <SEO
        title="Contact AR Infotech | Software License Support"
        description="Get in touch with AR Infotech for genuine Microsoft license support, bulk orders, or activation help. Call, WhatsApp, or email us."
      />

      {/* ─── HERO BANNER with blurred bg ─── */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 280, md: 380 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Blurred background image */}
        <Box
          component="img"
          src={bgImg}
          alt=""
          sx={{
            position: 'absolute',
            top: 0, left: 0, width: '100%', height: '100%',
            objectFit: 'cover',
            filter: 'blur(6px) brightness(0.45)',
            transform: 'scale(1.08)',
          }}
        />
        {/* Overlay */}
        <Box
          sx={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            background: 'linear-gradient(135deg, rgba(10,22,60,0.75) 0%, rgba(14,100,200,0.55) 100%)',
          }}
        />

        {/* Center content */}
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2, textAlign: 'center', py: { xs: 6, md: 8 } }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Chip
              label="💬 We're Here to Help"
              sx={{
                mb: 3, backgroundColor: 'rgba(0,210,196,0.15)',
                border: '1px solid rgba(0,210,196,0.5)',
                color: '#00D2C4', fontWeight: 700, fontSize: '0.85rem', px: 1
              }}
            />
            <Typography
              variant="h1"
              sx={{
                color: '#FFF', fontWeight: 900,
                fontSize: { xs: '2.5rem', md: '4rem' },
                mb: 2, textShadow: '0 2px 20px rgba(0,0,0,0.4)'
              }}
            >
              Contact Us
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255,255,255,0.85)', fontWeight: 400,
                maxWidth: 600, mx: 'auto', lineHeight: 1.7
              }}
            >
              Have a question about our software licenses? We're ready to help you get the right product activated.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* ─── MAIN CONTENT (Form + Info) floating on bg ─── */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(160deg, #EBF6FF 0%, #F5FAFF 60%, #FFFFFF 100%)',
          pt: { xs: 5, md: 8 },
          pb: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={isMobile ? 3 : 5} alignItems="stretch">

            {/* ── Contact Form (Left Column) ── */}
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                style={{ width: '100%', display: 'flex' }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, sm: 4.5 },
                    borderRadius: 3,
                    border: '1px solid rgba(14,106,218,0.12)',
                    boxShadow: '0 20px 60px rgba(14,106,218,0.08)',
                    background: '#FFFFFF',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: '#0e3a7a', fontSize: { xs: '1.5rem', md: '1.85rem' } }}>
                    Send Us a Message
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Fill in the form and our licensing specialists will get back to you within 1 business hour.
                  </Typography>

                  <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="Your Name" name="name" size="small"
                          value={formData.name} onChange={handleChange}
                          variant="outlined" required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="Email Address" name="email" type="email" size="small"
                          value={formData.email} onChange={handleChange}
                          variant="outlined" required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="Phone Number" name="phone" size="small"
                          value={formData.phone} onChange={handleChange}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth select label="Subject" name="subject" size="small"
                          value={formData.subject} onChange={handleChange}
                          variant="outlined"
                        >
                          <MenuItem value="order">Order Query</MenuItem>
                          <MenuItem value="product">License Activation Help</MenuItem>
                          <MenuItem value="wholesale">Bulk Licensing</MenuItem>
                          <MenuItem value="feedback">Feedback</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth multiline rows={5} label="Your Message" name="message" size="small"
                          value={formData.message} onChange={handleChange}
                          variant="outlined" required
                        />
                      </Grid>
                      <Grid item xs={12} sx={{ mt: 'auto', pt: 1 }}>
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          size="large"
                          endIcon={<Send />}
                          sx={{
                            py: 1.4, fontWeight: 700,
                            borderRadius: 2, fontSize: '0.98rem',
                            textTransform: 'none',
                            background: 'linear-gradient(135deg, #0e6ada 0%, #0099cc 100%)',
                            boxShadow: '0 6px 20px rgba(14,106,218,0.35)',
                            '&:hover': { boxShadow: '0 8px 28px rgba(14,106,218,0.5)' }
                          }}
                        >
                          Send Message
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Paper>
              </motion.div>
            </Grid>

            {/* ── Contact Info & Details (Right Column) ── */}
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.15 }}
                style={{ width: '100%', display: 'flex' }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, sm: 4.5 },
                    borderRadius: 3,
                    border: '1px solid rgba(14,106,218,0.12)',
                    boxShadow: '0 20px 60px rgba(14,106,218,0.08)',
                    background: '#FFFFFF',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: '#0e3a7a', fontSize: { xs: '1.5rem', md: '1.85rem' } }}>
                      Get In Touch
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      We are available Monday to Saturday to assist you with license selection, bulk pricing, and activation.
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.8 }}>
                      {contactInfo.map((info, i) => (
                        <Box
                          key={i}
                          component={Link}
                          href={info.href}
                          target={info.href.startsWith('http') ? '_blank' : undefined}
                          underline="none"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 1.5,
                            borderRadius: 2,
                            border: '1px solid #F1F5F9',
                            bgcolor: '#F8FAFC',
                            transition: 'all 0.25s ease',
                            textDecoration: 'none',
                            '&:hover': {
                              bgcolor: '#F0F7FF',
                              borderColor: 'rgba(14,106,218,0.3)',
                              transform: 'translateX(4px)'
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: 42,
                              height: 42,
                              borderRadius: '8px',
                              flexShrink: 0,
                              backgroundColor: `${info.color}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: info.color,
                            }}
                          >
                            {info.icon}
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5, display: 'block' }}>
                              {info.label}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', lineHeight: 1.3, mt: 0.2 }}>
                              {info.value}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/* Social links */}
                  <Box sx={{ pt: 3, mt: 2, borderTop: '1px solid #E2E8F0' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#0e3a7a' }}>
                      Follow AR Infotech
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      {[
                        { icon: <Instagram fontSize="small" />, color: '#E1306C', href: 'https://www.instagram.com/arinfotechsolutions' },
                        { icon: <Facebook fontSize="small" />, color: '#1877F2', href: 'https://www.facebook.com' },
                        { icon: <YouTube fontSize="small" />, color: '#FF0000', href: 'https://www.youtube.com/@arinfotechsolutions' },
                        { icon: <WhatsApp fontSize="small" />, color: '#25D366', href: 'https://wa.me/919113299131' },
                      ].map((s, i) => (
                        <IconButton
                          key={i} href={s.href} target="_blank"
                          size="small"
                          sx={{
                            width: 38, height: 38,
                            border: `1px solid ${s.color}35`,
                            color: s.color,
                            '&:hover': { backgroundColor: `${s.color}15`, borderColor: s.color }
                          }}
                        >
                          {s.icon}
                        </IconButton>
                      ))}
                    </Box>
                  </Box>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─── Google Map ─── */}
      <Box sx={{ height: { xs: 300, md: 420 }, width: '100%' }}>
        <iframe
          title="AR Infotech Location"
          src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3889.171164455281!2d77.57992207507517!3d12.896712987411837!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTLCsDUzJzQ4LjIiTiA3N8KwMzQnNTcuMCJF!5e0!3m2!1sen!2sin!4v1777893738927!5m2!1sen!2sin"
          width="100%"
          height="100%"
          style={{ border: 0, display: 'block' }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </Box>

      <Snackbar open={snackOpen} autoHideDuration={4000} onClose={() => setSnackOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackOpen(false)} severity="success" variant="filled" icon={<CheckCircle />}>
          Message sent! We'll get back to you shortly.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactPage;
